/**
 * DashboardWidgets.js  —  Grand Memories Resort & Spa PMS  —  Rich data visualisation toolkit
 * Pure SVG, zero external dependencies, fully respects CSS custom properties.
 */
import React, { useRef, useEffect, useState } from 'react';

export const fmtShort = (n) => {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000)     return (n / 1_000).toFixed(0) + 'K';
  return String(Math.round(n || 0));
};

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

function useWidth(fallback = 400) {
  const ref = useRef(null);
  const [w, setW] = useState(fallback);
  useEffect(() => {
    const measure = () => { if (ref.current) setW(ref.current.getBoundingClientRect().width || fallback); };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [fallback]);
  return [ref, w];
}

export function AreaChart({ data = [], color = '#2563eb', height = 160, formatY = fmtShort }) {
  const [ref, w] = useWidth(500);
  const [tip, setTip] = useState(null);
  if (!data.length) return <div ref={ref} style={{ height, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--textFaint)', fontSize:12 }}>No data yet</div>;
  const vals = data.map(d => typeof d === 'object' ? (d.value || 0) : d);
  const lbls = data.map(d => typeof d === 'object' ? (d.label || '') : '');
  const max = Math.max(...vals, 1);
  const pL = 42, pR = 8, pT = 14, pB = 26;
  const cw = w - pL - pR, ch = height - pT - pB;
  const tx = i => pL + (vals.length < 2 ? cw/2 : (i / (vals.length - 1)) * cw);
  const ty = v => pT + (1 - v / max) * ch;
  const pts = vals.map((v, i) => [tx(i), ty(v)]);
  const line = pts.map(([x, y]) => x + "," + y).join(" ");
  const area = "M" + pts[0][0] + "," + (pT+ch) + " " + pts.map(([x,y]) => "L"+x+","+y).join(" ") + " L" + pts[pts.length-1][0] + "," + (pT+ch) + "Z";
  const uid = "ag" + color.replace(/[^a-z0-9]/gi,"");
  return (
    <div ref={ref} style={{ position:"relative", width:"100%" }}>
      <svg width="100%" height={height}
        onMouseLeave={() => setTip(null)}
        onMouseMove={e => {
          const rect = e.currentTarget.getBoundingClientRect();
          const mx = (e.clientX - rect.left) * (w / rect.width);
          const idx = Math.round(clamp((mx - pL) / (cw / Math.max(vals.length - 1, 1)), 0, vals.length - 1));
          setTip({ idx, x: pts[idx][0], y: pts[idx][1], v: vals[idx], l: lbls[idx] });
        }}
      >
        <defs>
          <linearGradient id={uid} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.22"/>
            <stop offset="100%" stopColor={color} stopOpacity="0.02"/>
          </linearGradient>
        </defs>
        {[0, 0.25, 0.5, 0.75, 1].map((t, i) => {
          const y = ty(max * t);
          return (
            <g key={i}>
              <line x1={pL} y1={y} x2={pL+cw} y2={y} stroke="var(--border)" strokeWidth="0.5" strokeDasharray={i > 0 ? "3,5" : ""}/>
              <text x={pL-5} y={y+4} textAnchor="end" fontSize="9" fill="var(--textFaint)">{formatY(max * t)}</text>
            </g>
          );
        })}
        {lbls.map((l, i) => {
          if (!l) return null;
          const step = Math.max(1, Math.floor(vals.length / 7));
          if (i % step !== 0 && i !== vals.length - 1) return null;
          return <text key={i} x={tx(i)} y={height - 4} textAnchor="middle" fontSize="9" fill="var(--textFaint)">{l}</text>;
        })}
        <path d={area} fill={"url(#" + uid + ")"}/>
        <polyline points={line} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round"/>
        {tip && <>
          <line x1={tip.x} y1={pT} x2={tip.x} y2={pT+ch} stroke={color} strokeWidth="1" strokeDasharray="3,3" opacity="0.5"/>
          <circle cx={tip.x} cy={tip.y} r="4" fill={color} stroke="var(--bgCard)" strokeWidth="2"/>
        </>}
      </svg>
      {tip && (
        <div style={{ position:"absolute", top:6, left: clamp(tip.x - 36, 0, w - 90), background:"var(--bgCard)", border:"1px solid "+color, borderRadius:6, padding:"4px 10px", fontSize:11, fontWeight:700, pointerEvents:"none", boxShadow:"0 4px 16px "+color+"33", zIndex:10, color:"var(--textPrimary)" }}>
          <div style={{ fontSize:9, color:"var(--textFaint)" }}>{tip.l}</div>
          {formatY(tip.v)}
        </div>
      )}
    </div>
  );
}

export function BarChartV({ data = [], color = "#2563eb", height = 140, formatY = fmtShort }) {
  const [ref, w] = useWidth(400);
  const [hov, setHov] = useState(null);
  if (!data.length) return <div ref={ref} style={{ height }}/>;
  const vals = data.map(d => typeof d === "object" ? (d.value || 0) : d);
  const lbls = data.map(d => typeof d === "object" ? (d.label || "") : "");
  const cols = data.map(d => (typeof d === "object" && d.color) ? d.color : color);
  const max = Math.max(...vals, 1);
  const pL = 38, pR = 6, pT = 10, pB = 26;
  const cw = w - pL - pR, ch = height - pT - pB;
  const slot = cw / vals.length;
  const bw = Math.max(4, slot * 0.58);
  return (
    <div ref={ref} style={{ width:"100%" }}>
      <svg width="100%" height={height}>
        {[0, 0.5, 1].map(t => {
          const y = pT + (1-t)*ch;
          return <g key={t}>
            <line x1={pL} y1={y} x2={pL+cw} y2={y} stroke="var(--border)" strokeWidth="0.5" strokeDasharray={t > 0 ? "3,5" : ""}/>
            <text x={pL-4} y={y+4} textAnchor="end" fontSize="9" fill="var(--textFaint)">{formatY(t*max)}</text>
          </g>;
        })}
        <line x1={pL} y1={pT+ch} x2={pL+cw} y2={pT+ch} stroke="var(--borderMid)" strokeWidth="1"/>
        {vals.map((v, i) => {
          const barH = Math.max(2, (v / max) * ch);
          const x = pL + i * slot + (slot - bw) / 2;
          const y = pT + ch - barH;
          const c = cols[i];
          const isH = hov === i;
          return (
            <g key={i} onMouseEnter={() => setHov(i)} onMouseLeave={() => setHov(null)}>
              <rect x={x} y={y} width={bw} height={barH} fill={c} rx="3" opacity={isH ? 1 : hov != null ? 0.65 : 0.78}/>
              {isH && <text x={x+bw/2} y={y-5} textAnchor="middle" fontSize="9" fontWeight="700" fill={c}>{formatY(v)}</text>}
              {lbls[i] && <text x={x+bw/2} y={height-5} textAnchor="middle" fontSize="8" fill="var(--textFaint)">{lbls[i]}</text>}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export function DonutRing({ segments = [], size = 120, stroke = 14, centerLabel, centerSub }) {
  const total = segments.reduce((s, seg) => s + (seg.value || 0), 0) || 1;
  const r = (size - stroke) / 2;
  const cx = size / 2, cy = size / 2;
  const circ = 2 * Math.PI * r;
  let offset = 0;
  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:10 }}>
      <div style={{ position:"relative", width:size, height:size }}>
        <svg width={size} height={size} style={{ transform:"rotate(-90deg)" }}>
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--border)" strokeWidth={stroke}/>
          {segments.map((seg, i) => {
            const dash = (seg.value / total) * circ;
            const el = <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={seg.color} strokeWidth={stroke} strokeDasharray={dash + " " + (circ-dash)} strokeDashoffset={-offset}/>;
            offset += dash;
            return el;
          })}
        </svg>
        {(centerLabel != null || centerSub) && (
          <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
            {centerLabel != null && <div style={{ fontSize:size*0.17, fontWeight:800, color:"var(--textPrimary)", lineHeight:1 }}>{centerLabel}</div>}
            {centerSub && <div style={{ fontSize:size*0.1, color:"var(--textFaint)", marginTop:2 }}>{centerSub}</div>}
          </div>
        )}
      </div>
      <div style={{ display:"flex", gap:8, flexWrap:"wrap", justifyContent:"center" }}>
        {segments.map((seg, i) => (
          <div key={i} style={{ display:"flex", alignItems:"center", gap:4, fontSize:10, color:"var(--textMuted)" }}>
            <div style={{ width:7, height:7, borderRadius:"50%", background:seg.color, flexShrink:0 }}/>
            {seg.label}
          </div>
        ))}
      </div>
    </div>
  );
}

export function RankBars({ items = [], color = "#2563eb", formatVal = v => v }) {
  if (!items.length) return <div style={{ color:"var(--textFaint)", fontSize:12, padding:"10px 0" }}>No data</div>;
  const maxVal = Math.max(...items.map(i => i.value || 0), 1);
  return (
    <div style={{ display:"grid", gap:9 }}>
      {items.map((item, i) => {
        const pct = ((item.value || 0) / maxVal) * 100;
        const c = item.color || color;
        return (
          <div key={i}>
            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3, fontSize:11 }}>
              <span style={{ color:"var(--textSecond)", fontWeight:500, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:"66%" }}>
                <span style={{ color:"var(--textFaint)", marginRight:4, fontSize:9 }}>#{i+1}</span>{item.label}
              </span>
              <span style={{ color:c, fontWeight:700, flexShrink:0 }}>{formatVal(item.value)}</span>
            </div>
            <div style={{ height:4, background:"var(--border)", borderRadius:4, overflow:"hidden" }}>
              <div style={{ height:"100%", width:pct+"%", background:c, borderRadius:4, transition:"width .7s cubic-bezier(.16,1,.3,1)" }}/>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function Gauge({ value = 0, max = 100, color = "#2563eb", label, size = 110 }) {
  const pct = clamp(value / max, 0, 1);
  const cx = size / 2, cy = size * 0.58, r = size * 0.37;
  const arc = (from, to) => {
    const x1 = cx + r * Math.cos(from), y1 = cy + r * Math.sin(from);
    const x2 = cx + r * Math.cos(to),   y2 = cy + r * Math.sin(to);
    return "M"+x1+","+y1+" A"+r+","+r+" 0 "+(to-from > Math.PI ? 1 : 0)+",1 "+x2+","+y2;
  };
  const needle = -Math.PI + pct * Math.PI;
  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center" }}>
      <svg width={size} height={size * 0.64}>
        <path d={arc(-Math.PI, 0)} fill="none" stroke="var(--border)" strokeWidth="8" strokeLinecap="round"/>
        {pct > 0.01 && <path d={arc(-Math.PI, needle)} fill="none" stroke={color} strokeWidth="8" strokeLinecap="round"/>}
        <text x={cx} y={cy + 2} textAnchor="middle" fontSize={size * 0.17} fontWeight="800" fill="var(--textPrimary)">{Math.round(pct * 100)}%</text>
        {label && <text x={cx} y={cy + size * 0.14} textAnchor="middle" fontSize={size * 0.1} fill="var(--textFaint)">{label}</text>}
      </svg>
    </div>
  );
}

export function ActivityFeed({ items = [], maxItems = 10 }) {
  if (!items.length) return <div style={{ color:"var(--textFaint)", fontSize:12, padding:"16px 0", textAlign:"center" }}>No recent activity</div>;
  return (
    <div>
      {items.slice(0, maxItems).map((item, i) => (
        <div key={i} style={{ display:"flex", gap:10, padding:"9px 0", borderBottom: i < Math.min(items.length,maxItems) - 1 ? "1px solid var(--border)" : "none" }}>
          <div style={{ width:28, height:28, borderRadius:"50%", flexShrink:0, background:(item.color||"#2563eb")+"15", border:"1px solid "+(item.color||"#2563eb")+"30", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13 }}>
            {item.icon || "◈"}
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:12, color:"var(--textSecond)", fontWeight:500, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{item.text}</div>
            {item.sub && <div style={{ fontSize:10, color:"var(--textFaint)", marginTop:1 }}>{item.sub}</div>}
          </div>
          {item.time && <div style={{ fontSize:10, color:"var(--textFaint)", flexShrink:0, paddingTop:2 }}>{item.time}</div>}
        </div>
      ))}
    </div>
  );
}

export function DashCard({ children, title, titleColor, action, style = {} }) {
  return (
    <div style={{ background:"var(--bgCard)", border:"1px solid var(--border)", borderRadius:12, padding:"16px 18px", minWidth:0, overflow:"hidden", boxShadow:"0 1px 6px rgba(0,0,0,0.05)", ...style }}>
      {(title || action) && (
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12, paddingBottom:9, borderBottom:"1px solid var(--border)" }}>
          <div style={{ fontSize:12, fontWeight:700, textTransform:"uppercase", letterSpacing:.8, color: titleColor || "var(--textMuted)" }}>{title}</div>
          {action && <div>{action}</div>}
        </div>
      )}
      {children}
    </div>
  );
}

export function KpiCard({ label, value, sub, color = "#2563eb", icon, spark = [], style = {} }) {
  const maxS = spark.length ? Math.max(...spark, 1) : 1;
  return (
    <div style={{ background:"var(--bgCard)", border:"1px solid var(--border)", borderTop:"3px solid "+color, borderRadius:12, padding:"16px 18px 12px", position:"relative", overflow:"hidden", minWidth:0, transition:"box-shadow .2s", ...style }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = "0 6px 24px "+color+"20"}
      onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
    >
      <div style={{ position:"absolute", top:-24, right:-24, width:80, height:80, borderRadius:"50%", background:color, opacity:0.05, pointerEvents:"none" }}/>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6 }}>
        <div style={{ fontSize:12, color:"var(--textFaint)", fontWeight:700, textTransform:"uppercase", letterSpacing:.8 }}>{label}</div>
        {icon && <div style={{ fontSize:16, opacity:0.55 }}>{icon}</div>}
      </div>
      <div style={{ fontSize:26, fontWeight:800, color:"var(--textPrimary)", letterSpacing:-1, lineHeight:1, marginBottom:6 }}>{value}</div>
      {spark.length > 1 && (() => {
        const pts = spark.map((v,j) => [j*8+4, 18-(v/maxS)*15]);
        const ln = pts.map(([x,y]) => x+","+y).join(" ");
        const ar = "M"+pts[0][0]+",18 "+pts.map(([x,y])=>"L"+x+","+y).join(" ")+" L"+pts[pts.length-1][0]+",18Z";
        const uid = "kpi"+color.replace(/[^a-z0-9]/gi,"");
        return (
          <svg viewBox={"0 0 " + (spark.length * 8) + " 20"} style={{ width:"100%", height:20, marginBottom:4 }} preserveAspectRatio="none">
            <defs>
              <linearGradient id={uid} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity="0.3"/>
                <stop offset="100%" stopColor={color} stopOpacity="0"/>
              </linearGradient>
            </defs>
            <path d={ar} fill={"url(#"+uid+")"}/>
            <polyline points={ln} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round"/>
            <circle cx={pts[pts.length-1][0]} cy={pts[pts.length-1][1]} r="2.5" fill={color}/>
          </svg>
        );
      })()}
      {sub && <div style={{ fontSize:10, color:"var(--textFaint)" }}>{sub}</div>}
    </div>
  );
}
